<template>
  <div class="container mt-4">
    <TopNav>
      <div
        v-if="isQuizGradable"
        class="d-flex flex-row justify-content-end align-items-center"
      >
        <h3 class="mb-0 mr-2">{{ submissionScore }}</h3>
        <div
          v-if="!isReadOnly"
          v-tooltip="{
            content: allAnswersGraded
              ? ''
              : $t('QUIZZES.TEXT.GRADE_ALL_ANSWERS_TO_FINISH'),
            delay: 100,
            placement: 'bottom',
            container: 'body',
            trigger: 'click hover',
          }"
        >
          <HMButton
            color="brand"
            :disabled="!allAnswersGraded"
            @click="finishGrading"
          >
            {{ $t('QUIZZES.BUTTON.FINISH') }}
          </HMButton>
        </div>
      </div>
    </TopNav>
    <div v-if="quizSubmission && quizSubmission.quiz_snapshot">
      <h2 class="my-4">{{ quizSubmission.student.full_name }}</h2>
      <section
        v-for="(question, index) in quizSubmission.quiz_snapshot.quiz_questions"
        :key="question.id"
        class="mb-5"
      >
        <div>{{ $t('QUIZZES.LABEL.QUESTION') }} {{ index + 1 }}:</div>
        <hr class="mt-1" />
        <h3>
          {{ question.question_title }}
        </h3>
        <div v-if="question.question_text">
          <HMCustomContent :json="question.question_text" html="" />
        </div>
        <div
          class="d-flex answer d-flex"
          :class="{
            'flex-row': question.question_type === 'single_image',
            'flex-column': question.question_type !== 'single_image',
            'flex-wrap': question.question_type !== 'file_upload',
          }"
        >
          <div v-for="answer in question.answers" :key="answer.id">
            <HMOpinionScalePreview
              v-if="answer.answer_type === 'scale'"
              :scaleSelectedItem="scaleSelectedItem(question.id)"
              :questionRange="getQuestionRange(question)"
              :startText="answer.start_text"
              :endText="answer.end_text"
            />
            <HMCheckBoxGroupPreview
              v-else-if="answer.answer_type === 'check'"
              :selected="studentAnswered(question.id, answer.id)"
              :correct="answer.is_correct"
              :label="answer.answer_label"
            />
            <HMImageCheckBoxGroupPreview
              class="mr-2"
              v-else-if="
                answer.answer_type === 'image_check' && answer.answer_image
              "
              :selected="studentAnswered(question.id, answer.id)"
              :correct="answer.is_correct"
              :label="answer.answer_label"
              :img="answer.answer_image.thumbnail"
            />
            <div v-else-if="answer.answer_type === 'upload'">
              <div v-if="getStudentAnswer(question.id)">
                <HMFilePreview
                  class="mb-3"
                  v-for="upload in getStudentAnswer(question.id).uploads"
                  :file="upload.file"
                  :key="upload.id"
                />
              </div>
              <div v-else>
                {{ $t('QUIZZES.TEXT.NO_UPLOADS') }}
              </div>
            </div>
            <div v-else-if="answer.answer_type === 'text'">
              <span v-if="getStudentAnswer(question.id)">{{
                getStudentAnswer(question.id).text
              }}</span>
            </div>
            <div
              class="d-flex flex-row justify-content-start align-items-center mt-4"
              v-if="
                getStudentAnswer(question.id) &&
                !question.is_auto_gradable &&
                question.manual_check
              "
            >
              <HMButton
                v-if="!isReadOnly || isStudentAnswerCorrect(question.id)"
                :disabled="isReadOnly"
                class="mr-3"
                :color="
                  isStudentAnswerCorrect(question.id)
                    ? 'selected'
                    : 'outline-gray'
                "
                @click="markCorrect(question, answer)"
              >
                <span>
                  <i
                    class="far fa-check mr-2"
                    :class="{
                      'c-coral':
                        isStudentAnswerUngraded(question.id) ||
                        isStudentAnswerIncorrect(question.id),
                    }"
                  ></i>
                  {{ $t('QUIZZES.BUTTON.CORRECT') }}
                </span>
              </HMButton>
              <HMButton
                v-if="!isReadOnly || isStudentAnswerIncorrect(question.id)"
                :disabled="isReadOnly"
                :color="
                  isStudentAnswerIncorrect(question.id)
                    ? 'selected-incorrect'
                    : 'outline-gray'
                "
                @click="markIncorrect(question, answer)"
              >
                <span>
                  <i
                    class="far fa-times mr-2"
                    :class="{
                      'c-coral':
                        isStudentAnswerUngraded(question.id) ||
                        isStudentAnswerCorrect(question.id),
                    }"
                  ></i>
                  {{ $t('QUIZZES.BUTTON.INCORRECT') }}
                </span>
              </HMButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { errorToMessage } from '@/core/utils/errors';
import TopNav from '@/web/components/navigation/TopNav';
import HMButton from '@/web/components/common/HMButton';
import HMFilePreview from '@/web/components/common/HMFilePreview';
import HMDialog from '@/web/components/common/HMDialog.vue';

export default {
  name: 'QuizSubmissionViewer',
  components: { HMFilePreview, HMButton, TopNav },
  computed: {
    submissionScore() {
      if (!this.quizSubmission) return '';

      const score = this.quizSubmission.grade_achieved_score || 0;
      return `${score}%`;
    },
    allAnswersGraded() {
      if (!this.quizSubmission?.student_answers) return true;
      return this.quizSubmission.student_answers.every(
        answer =>
          answer.is_correct !== null ||
          this.questionNotGradable(answer.quiz_question)
      );
    },
    isQuizGradable() {
      return this.quizSubmission?.quiz_snapshot?.is_gradable;
    },
    isReadOnly() {
      return this.quizSubmission.is_graded || !this.isQuizGradable;
    },
  },
  data() {
    return {
      quizSubmission: null,
    };
  },
  methods: {
    questionNotGradable(questionId) {
      const questions = this.quizSubmission.quiz_snapshot.quiz_questions.filter(
        q => q.id === questionId
      );
      if (questions && questions.length) {
        return !questions[0].manual_check;
      }
      return false;
    },
    getStudentAnswers(questionId) {
      return this.quizSubmission.student_answers.filter(
        a => a.quiz_question === questionId
      );
    },
    getStudentAnswer(questionId) {
      const answers = this.getStudentAnswers(questionId);
      if (!answers.length) return null;
      return answers[0];
    },
    isStudentAnswerCorrect(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === true;
    },
    isStudentAnswerIncorrect(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === false;
    },
    isStudentAnswerUngraded(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === null;
    },
    studentAnswered(questionId, answerId) {
      return (
        this.quizSubmission.student_answers.filter(
          a => a.quiz_question === questionId && a.selected_answer === answerId
        ).length > 0
      );
    },
    async finishGrading() {
      try {
        const payload = {
          quizId: this.quizSubmission.quiz,
          submissionId: this.quizSubmission.id,
        };
        await this.$store.dispatch('quiz/finishGradingQuizSubmission', payload);
        this.$router.go(-1);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Finish grading failed',
          text: errorToMessage(
            error?.response,
            `${this.$t('NOTIFICATIONS.TEXT.REQUEST_FAILED')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async autoGradeQuizSubmission() {
      try {
        const payload = {
          quizId: this.quizSubmission.quiz,
          submissionId: this.quizSubmission.id,
        };
        this.quizSubmission = await this.$store.dispatch(
          'quiz/autoGradeQuizSubmission',
          payload
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Auto grading failed',
          text: errorToMessage(
            error?.response,
            `${this.$t('NOTIFICATIONS.TEXT.REQUEST_FAILED')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async markCorrect(question, answer) {
      const studentAnswer = this.getStudentAnswer(question.id);
      if (!studentAnswer) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'It seems student answer does not exist for this question',
          duration: -1,
        });
        return;
      }
      try {
        const payload = {
          quizId: this.quizSubmission.quiz,
          submissionId: this.quizSubmission.id,
          answerId: studentAnswer.id,
        };
        const quizGradeScore = await this.$store.dispatch(
          'quiz/markSolvedAnswerCorrect',
          payload
        );
        console.log('quizGradeScore=', quizGradeScore);
        this.quizSubmission.grade_achieved_score =
          quizGradeScore.grade_achieved_score;
        this.quizSubmission.grade_total_score =
          quizGradeScore.grade_total_score;
        if (studentAnswer) {
          studentAnswer.is_correct = true;
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to mark the answer as correct',
          text: errorToMessage(
            error?.response,
            `${this.$t('NOTIFICATIONS.TEXT.REQUEST_FAILED')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async markIncorrect(question, answer) {
      const studentAnswer = this.getStudentAnswer(question.id);
      if (!studentAnswer) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'It seems student answer does not exist for this question',
          duration: -1,
        });
        return;
      }
      try {
        const payload = {
          quizId: this.quizSubmission.quiz,
          submissionId: this.quizSubmission.id,
          answerId: studentAnswer.id,
        };
        const quizGradeScore = await this.$store.dispatch(
          'quiz/markSolvedAnswerIncorrect',
          payload
        );
        console.log('quizGradeScore=', quizGradeScore);
        this.quizSubmission.grade_achieved_score =
          quizGradeScore.grade_achieved_score;
        this.quizSubmission.grade_total_score =
          quizGradeScore.grade_total_score;
        if (studentAnswer) {
          studentAnswer.is_correct = false;
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to mark the answer as correct',
          text: errorToMessage(
            error?.response,
            `${this.$t('NOTIFICATIONS.TEXT.REQUEST_FAILED')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    scaleSelectedItem(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (answer) {
        return answer.range;
      }
    },
    getQuestionRange(question) {
      const answer = question.answers[0];
      if (answer) {
        return answer.range;
      }
    },
    showAutoGradeQuizSubmission() {
      this.$modal.show(
        HMDialog,
        {
          title: this.$t('QUIZZES.TITLE.AUTO_GRADE_QUIZ'),
          subtitle: null,
          text: this.$t('QUIZZES.TEXT.THE_QUIZ_SUBMISSION_HAS_NOT_BEEN'),
          discardButton: this.$t('GENERAL.BUTTON.GO_BACK'),
          discard: () => {
            this.$emit('close');
            this.$router.go(-1);
          },
          confirm: this.autoGradeQuizSubmission,
          closable: false,
        },
        { clickToClose: false }
      );
    },
    async loadQuizSubmission() {
      const quizId = this.$route.params.id;
      const submissionId = this.$route.params.submission_id;
      try {
        this.quizSubmission = await this.$store.dispatch(
          'quiz/getQuizSubmission',
          {
            quizId,
            submissionId,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error?.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ_SUBMISSION'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
  },
  async mounted() {
    await this.loadQuizSubmission();
    const isGraded = this.quizSubmission.is_graded;
    const isCompleted = this.quizSubmission.completed;
    const isGradable = this.quizSubmission.quiz_snapshot.is_gradable;
    if (!isCompleted && !isGraded && isGradable) {
      this.showAutoGradeQuizSubmission();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.correct-answer {
  background-color: lighten($light-green, 20%);
}
</style>
